import { useMemo, useState } from 'react'
import { UserPlus, HelpCircle } from '@/ui/icons'
import { useTripDetailPanel } from '../contexts/TripDetailPanelContext'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { CREW_TOOLTIP_COPY } from '@/shared/copy/tooltips'

const INITIAL_CREW_VISIBLE = 4
import { getUserProfileUrl } from '@/shared/utils/userImages'
import { SectionCard } from './SectionCard'
import { Button } from '@/ui/design-system'
import { cn } from '@/shared/utils'
import type { MemberResponse } from '@/shared/api'

/** Partition members into primary (vehicles) and tag-alongs grouped by parent */
function useCrewHierarchy(members: MemberResponse[]) {
  return useMemo(() => {
    const primary = members.filter((m) => !m.parent_member_id)
    const tagAlongsByParent = members
      .filter((m) => m.parent_member_id != null)
      .reduce<Record<string, MemberResponse[]>>((acc, m) => {
        const pid = m.parent_member_id!
        if (!acc[pid]) acc[pid] = []
        acc[pid].push(m)
        return acc
      }, {})
    return { primary, tagAlongsByParent }
  }, [members])
}

function getDisplayName(member: MemberResponse): string {
  if (member.guest_name) return member.guest_name
  const name = member.user?.user_metadata?.full_name ?? member.user?.email
  return name ?? 'Crew member'
}

function getInitials(member: MemberResponse): string {
  if (member.guest_name) {
    const parts = member.guest_name.trim().split(/\s+/)
    return (parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '').toUpperCase()
  }
  const name = member.user?.user_metadata?.full_name ?? member.user?.email ?? ''
  const parts = String(name).trim().split(/\s+/)
  return (parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '').toUpperCase() || '?'
}

function getVehicleDisplay(member: MemberResponse): string | null {
  if (!member.primary_vehicle) return null
  const { make, model, year } = member.primary_vehicle
  return [year, make, model].filter(Boolean).join(' ')
}

interface CrewSummarySectionProps {
  members: MemberResponse[]
  /** Max crew size from trip; placeholders fill slots up to this limit */
  crewLimit?: number | null
  title?: string
  className?: string
  /** When true and onInviteClick or inviteAction is set, shows an Invite control in the header */
  isOwner?: boolean
  /** Called when the Invite button is clicked (e.g. open share/invite modal). Ignored if inviteAction is set. */
  onInviteClick?: () => void
  /** Optional custom invite control (e.g. InviteDropdown). When set, used as header action instead of the default Invite button. */
  inviteAction?: React.ReactNode
}

export function CrewSummarySection({
  members,
  crewLimit = null,
  title = 'Crew',
  className,
  isOwner,
  onInviteClick,
  inviteAction
}: CrewSummarySectionProps) {
  const { setSelectedDetail, selectedDetail } = useTripDetailPanel()
  const currentCrewId = selectedDetail?.type === 'crew' ? selectedDetail.id : null
  const [expanded, setExpanded] = useState(false)

  const memberList = members ?? []
  const { primary, tagAlongsByParent } = useCrewHierarchy(memberList)
  const primaryCount = primary.length
  const totalPeople = memberList.length
  const placeholderCount =
    crewLimit != null && crewLimit > 0 ? Math.max(0, crewLimit - primaryCount) : 0
  const totalSlots = primaryCount + placeholderCount
  const maxVisible = expanded ? totalSlots : Math.min(totalSlots, INITIAL_CREW_VISIBLE)
  const primaryVisible = Math.min(primaryCount, maxVisible)
  const placeholdersVisible = Math.max(0, Math.min(placeholderCount, maxVisible - primaryVisible))
  const hasMoreCrew = totalSlots > INITIAL_CREW_VISIBLE
  const moreCount = totalSlots - INITIAL_CREW_VISIBLE

  const headerActions =
    (inviteAction ?? (isOwner && onInviteClick)) ? (
      inviteAction ?? (
        <Button variant="outline" size="sm" onClick={onInviteClick} className="gap-1.5">
          <UserPlus className="h-4 w-4" aria-hidden />
          Add
        </Button>
      )
    ) : null

  const sectionTitle = (
    <>
      <span>{title}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="What is the crew section?"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-xs">
          <p className="text-sm text-muted-foreground leading-relaxed">{CREW_TOOLTIP_COPY}</p>
        </TooltipContent>
      </Tooltip>
    </>
  )

  return (
    <SectionCard
      title={sectionTitle}
      count={totalPeople}
      actions={headerActions}
      className={className}
      contentClassName="pt-2"
      fixedHeight
    >
      {totalSlots === 0 ? (
        <p className="text-sm text-earth-500 py-1.5">No crew members yet.</p>
      ) : (
        <div className="space-y-0.5">
          <ul className="space-y-0.5">
            {primary.slice(0, primaryVisible).map((member) => {
              const tagAlongs = tagAlongsByParent[member.id] ?? []
              const isSelected = currentCrewId === member.id
              const vehicle = getVehicleDisplay(member)
              const avatarUrl =
                member.member_type === 'full_member' && member.user_profile?.profile_image_path
                  ? getUserProfileUrl(member.user_profile.profile_image_path)
                  : null
              return (
                <li key={member.id} className="space-y-0.5">
                  <button
                    type="button"
                    onClick={() => setSelectedDetail({ type: 'crew', id: member.id })}
                    className={cn(
                      'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                      isSelected ? 'bg-adventure-100 text-adventure-900 dark:bg-adventure-900/30 dark:text-adventure-200' : 'text-foreground hover:bg-earth-100 dark:hover:bg-earth-200'
                    )}
                  >
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0 border border-earth-200 dark:border-earth-600" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-adventure-100 text-adventure-800 dark:bg-adventure-900/40 dark:text-adventure-300 flex items-center justify-center text-xs font-semibold flex-shrink-0 border border-earth-200 dark:border-earth-600">
                        {getInitials(member)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{getDisplayName(member)}</div>
                      <div className="text-xs text-earth-500 dark:text-earth-400 truncate flex items-center gap-1.5">
                        <span className="capitalize">{member.role}</span>
                        {vehicle && (
                          <>
                            <span>·</span>
                            <span>{vehicle}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                  {tagAlongs.map((ta) => {
                    const taSelected = currentCrewId === ta.id
                    const taAvatarUrl =
                      ta.member_type === 'full_member' && ta.user_profile?.profile_image_path
                        ? getUserProfileUrl(ta.user_profile.profile_image_path)
                        : null
                    return (
                      <button
                        key={ta.id}
                        type="button"
                        onClick={() => setSelectedDetail({ type: 'crew', id: ta.id })}
                        className={cn(
                          'w-full flex items-center gap-2 rounded-md pl-8 pr-2 py-1.5 text-left text-sm transition-colors',
                          taSelected ? 'bg-adventure-100 text-adventure-900 dark:bg-adventure-900/30 dark:text-adventure-200' : 'text-foreground hover:bg-earth-100 dark:hover:bg-earth-200'
                        )}
                      >
                        {taAvatarUrl ? (
                          <img src={taAvatarUrl} alt="" className="h-6 w-6 rounded-full object-cover flex-shrink-0 border border-earth-200 dark:border-earth-600" />
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-earth-200 text-earth-700 dark:bg-earth-700 dark:text-earth-300 flex items-center justify-center text-xs font-medium flex-shrink-0 border border-earth-200 dark:border-earth-600">
                            {getInitials(ta)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-earth-700 dark:text-earth-300">{getDisplayName(ta)}</div>
                        </div>
                      </button>
                    )
                  })}
                </li>
              )
            })}
            {placeholdersVisible > 0 &&
              Array.from({ length: placeholdersVisible }, (_, i) => (
                <li
                  key={`placeholder-${i}`}
                  className={i > 0 || primaryVisible > 0 ? 'mt-2' : undefined}
                >
                  <div
                    className={cn(
                      'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm border border-dashed border-earth-200 dark:border-earth-600 bg-earth-50/50 dark:bg-earth-900/20',
                      isOwner && onInviteClick && 'cursor-pointer hover:border-adventure-300 hover:bg-adventure-50/50 dark:hover:border-adventure-700 dark:hover:bg-adventure-900/20'
                    )}
                    role={isOwner && onInviteClick ? 'button' : undefined}
                    onClick={isOwner && onInviteClick ? onInviteClick : undefined}
                    onKeyDown={
                      isOwner && onInviteClick
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onInviteClick()
                            }
                          }
                        : undefined
                    }
                    tabIndex={isOwner && onInviteClick ? 0 : undefined}
                    aria-label="Empty crew slot, click to invite"
                  >
                    <div className="h-8 w-8 rounded-full border-2 border-dashed border-earth-300 dark:border-earth-600 flex items-center justify-center flex-shrink-0 bg-earth-100 dark:bg-earth-800">
                      <UserPlus className="h-4 w-4 text-earth-400 dark:text-earth-500" aria-hidden />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-earth-500 dark:text-earth-400">Empty vehicle slot</div>
                      <div className="text-xs text-earth-400 dark:text-earth-500">
                        {isOwner && onInviteClick ? 'Click to invite' : '—'}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
          {hasMoreCrew && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-2 text-xs text-adventure-600 hover:text-adventure-700 dark:text-foreground dark:hover:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-1 rounded"
              aria-expanded={expanded}
              aria-label={expanded ? 'Show fewer crew members' : `Show ${moreCount} more`}
            >
              {expanded ? 'Show less' : `Show more (${moreCount} more)`}
            </button>
          )}
        </div>
      )}
    </SectionCard>
  )
}
